import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, Target, Instagram, ArrowLeft, Sparkles, MessageCircle, Handshake } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollProgress } from "@/components/ScrollProgress";
import { CookieBanner } from "@/components/CookieBanner";
import { VOCI } from "@/data/events";
import { SOCIAL } from "@/data/sponsors";
import { SectionsIndex } from "@/components/SectionsIndex";
import { StoryInvite } from "@/components/HomeSections";
import { useSeo } from "@/hooks/useSeo";

const PILLARS = [
  {
    icon: MessageCircle,
    titolo: "Parola condivisa",
    testo:
      "Ogni mese una socia racconta obiettivi, ostacoli e risultati: niente storytelling patinato, solo esperienza reale.",
  },
  {
    icon: Handshake,
    titolo: "Guida reciproca",
    testo:
      "Le più esperte affiancano chi inizia. Un percorso di mentorship informale che nasce dalle relazioni della rete.",
  },
  {
    icon: Sparkles,
    titolo: "Divulgazione",
    testo:
      "Le storie diventano contenuti social e incontri pubblici, per far arrivare il messaggio ad altre donne.",
  },
];

export default function Community() {
  useSeo({
    title: "Community — DonnaImpresa | Storie e obiettivi di imprenditrici",
    description:
      "La community di DonnaImpresa: storie, obiettivi ed esperienze di imprenditrici che diventano linee guida per altre donne. Partecipa e racconta la tua.",
    path: "/community",
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="community-page">
      <ScrollProgress />
      <Navbar />

      <main>
        <section className="grain relative overflow-hidden pt-36 sm:pt-40">
          <div className="pointer-events-none absolute -right-24 top-16 h-[320px] w-[320px] rounded-full bg-[#E0708A]/20 blur-[110px] di-blob" />
          <div className="relative mx-auto max-w-[1400px] px-5 pb-14 sm:px-10 lg:pb-20">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#6B5E62] di-underline"
              data-testid="community-back-home"
            >
              <ArrowLeft size={14} /> Home
            </Link>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-8 max-w-4xl font-display text-4xl leading-[1.02] tracking-tight text-[#1A1617] sm:text-5xl lg:text-6xl"
              data-testid="community-title"
            >
              Una community di donne
              <br />
              che <span className="di-shine italic">si fanno da guida</span>.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-7 max-w-2xl text-base leading-relaxed text-[#6B5E62]"
            >
              Crediamo che raccontare i propri obiettivi ad alta voce sia il
              primo passo per raggiungerli — e per far capire ad altre donne che
              è possibile. Qui raccogliamo le esperienze della rete.
            </motion.p>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-5 pb-6 sm:px-10">
          <div className="grid gap-5 md:grid-cols-3" data-testid="community-pillars">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.titolo}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`community-pillar-${i}`}
                className="rounded-2xl border border-[#E8E2E4] bg-white p-7 shadow-[0_8px_32px_rgba(94,42,82,0.05)] transition-transform duration-300 hover:-translate-y-1"
              >
                <p.icon size={22} className="text-[#E0708A]" />
                <h2 className="mt-5 font-display text-2xl text-[#1A1617]">
                  {p.titolo}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#6B5E62]">
                  {p.testo}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-10 lg:py-24">
          <h2 className="font-display text-3xl tracking-tight text-[#1A1617] sm:text-4xl">
            Le voci della rete
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-testid="community-voices">
            {VOCI.map((v, i) => (
              <motion.blockquote
                key={v.id}
                data-testid={`community-voice-${v.id}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                className="rounded-2xl border border-[#E8E2E4] bg-white p-7 shadow-[0_8px_32px_rgba(94,42,82,0.05)] transition-transform duration-300 hover:-translate-y-1.5 sm:p-9"
              >
                <Quote size={26} className="text-[#E0708A]/50" />
                <p className="mt-5 font-display text-xl italic leading-snug text-[#1A1617] sm:text-2xl">
                  {v.testo}
                </p>
                <footer className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-[#E8E2E4] pt-5">
                  <div>
                    <p className="text-sm font-semibold text-[#5E2A52]">{v.nome}</p>
                    <p className="text-xs text-[#6B5E62]">{v.ruolo}</p>
                  </div>
                  <p className="inline-flex items-center gap-2 rounded-full bg-[#E0708A]/10 px-4 py-2 text-xs text-[#5E2A52]">
                    <Target size={13} /> {v.obiettivo}
                  </p>
                </footer>
              </motion.blockquote>
            ))}
          </div>

          <div className="di-marquee-wrap mt-14 overflow-hidden">
            <div className="di-marquee-slow flex w-max items-center gap-8">
              {["crederci", "condividere", "guidare", "crescere insieme", "fare rete", "ispirare"]
                .concat(["crederci", "condividere", "guidare", "crescere insieme", "fare rete", "ispirare"])
                .map((w, i) => (
                  <span
                    key={i}
                    className="whitespace-nowrap font-display text-3xl italic text-[#5E2A52]/25 sm:text-5xl"
                  >
                    {w} <span className="not-italic text-[#E0708A]/40">·</span>
                  </span>
                ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-5 pb-20 sm:px-10 lg:pb-28">
          <div className="rounded-[28px] bg-[#5E2A52] px-7 py-12 sm:px-12 sm:py-16">
            <h2 className="max-w-2xl font-display text-3xl leading-tight text-white sm:text-4xl">
              Vuoi raccontare la tua storia alla rete?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">
              Compila il modulo di adesione: ti contattiamo per costruire
              insieme il tuo racconto e pubblicarlo sui nostri canali.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/racconta-la-tua-storia"
                data-testid="community-join-cta"
                className="rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#5E2A52] transition-colors duration-300 hover:bg-[#E0708A] hover:text-white"
              >
                Racconta la tua storia
              </Link>
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="community-instagram-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
              >
                <Instagram size={16} /> Seguici su Instagram
              </a>
            </div>
          </div>
        </section>
        <SectionsIndex />
        <StoryInvite />
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
}
